import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsPulldownService, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { take } from 'rxjs/operators';
import { SwapDataService, downloadCsv } from '../../../../services/swap-data.service';
import { SwapDetailComponent } from './swap-detail/swap-detail.component';

// =====================================================================
// SwapManagementComponent — Scope 6.2 list screen (6.2.1)
// ---------------------------------------------------------------------
// Stat cards (click one to filter by that status) + rms-table of swaps.
// Clicking a Swap # opens SwapDetailComponent in a pulldown, same
// pattern as Outbound Management.
// =====================================================================

interface SwapRow {
  SwapRequestID: number;
  SwapNumber: string;
  RequestDate: string;
  Priority: string;
  TargetDate: string | null;
  RequestingCustomer: string;
  RequestedCoffee: string;
  BagsRequested: number;
  SourceCustomer: string | null;
  SourceReserveOrderID: number | null;
  SourceCoffee: string | null;
  ProposedBags: number | null;
  ApprovedBags: number | null;
  DestinationWarehouse: string | null;
  SwapStatusID: number;
  StatusName: string;
  ApprovingTraderName: string | null;
  RequestedByName: string | null;
  MatchType: string | null;
  MatchScore: number | null;
  CreatedOrderID: number | null;
}

interface StatCard {
  label: string;
  statusId: number | null;   // null = All
  countKey: string;
  cssClass: string;
}

@Component({
  selector: 'app-swap-management',
  imports: [RmsModule],
  templateUrl: './swap-management.component.html',
  styleUrl: './swap-management.component.scss'
})
export class SwapManagementComponent implements OnInit {
  // rms-table has no ngOnChanges on columns and needs them ready before it
  // renders — so the table sits behind this flag (known rms-table quirk).
  Loaded = false;

  AllRows: SwapRow[] = [];
  // Kept as a real array (not a getter) so rms-table's data setter only
  // fires when the filter actually changes, not on every change detection.
  FilteredRows: SwapRow[] = [];
  Columns: Column[] = [];
  Counts: any = {};

  ActiveStatusFilter: number | null = null;
  MineOnly = false;
  Exporting = false;

  StatCards: StatCard[] = [
    { label: 'All', statusId: null, countKey: 'Total', cssClass: 'card-all' },
    { label: 'Draft', statusId: 1, countKey: 'Draft', cssClass: 'card-draft' },
    { label: 'Pending Approval', statusId: 2, countKey: 'PendingApproval', cssClass: 'card-pending' },
    { label: 'Returned', statusId: 3, countKey: 'Returned', cssClass: 'card-returned' },
    { label: 'Approved', statusId: 4, countKey: 'Approved', cssClass: 'card-approved' },
    { label: 'Completed', statusId: 6, countKey: 'Completed', cssClass: 'card-completed' },
    { label: 'Rejected', statusId: 5, countKey: 'Rejected', cssClass: 'card-rejected' },
  ];

  Buttons: HeaderButton[] = [
    { label: 'Audit Report (CSV)', onAction: () => this.exportAuditReport() }
  ];

  constructor(
    private swapData: SwapDataService,
    private pulldownService: RmsPulldownService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this.Columns = this.createColumns();
    await this.loadList();
    this.Loaded = true;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  async loadList() {
    try {
      const result = await lastValueFrom(this.swapData.getRequests({ MineOnly: this.MineOnly }));
      this.AllRows = result?.Requests ?? [];
      this.Counts = result?.Counts ?? {};
      this.applyFilter();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load swap requests.');
    }
  }

  applyFilter() {
    this.FilteredRows = this.ActiveStatusFilter === null
      ? [...this.AllRows]
      : this.AllRows.filter(r => r.SwapStatusID === this.ActiveStatusFilter);
    this.cdr.markForCheck();
  }

  selectStatusCard(card: StatCard) {
    this.ActiveStatusFilter = card.statusId;
    this.applyFilter();
  }

  async toggleMineOnly() {
    this.MineOnly = !this.MineOnly;
    await this.loadList();
  }

  // ---------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------

  createColumns(): Column[] {
    return [
      { key: 'SwapNumber', label: 'Swap #', hidden: false, clickable: true, onActionKey: 'openSwap', onAction: (row) => this.openSwap(row) },
      { key: 'SwapTypeName', label: 'Type', type: 'select', SearchType: 'select', hidden: false },
      { key: 'RequestDate', label: 'Requested', type: 'date', SearchType: 'date', hidden: false },
      { key: 'Priority', label: 'Priority', type: 'select', SearchType: 'select', hidden: false },
      { key: 'RequestingCustomer', label: 'Requesting Customer', hidden: false },
      { key: 'RequestedCoffee', label: 'Coffee Needed', hidden: false },
      { key: 'BagsRequested', label: 'Bags', type: 'number', hidden: false },
      { key: 'SourceCustomer', label: 'Source Customer', hidden: false },
      { key: 'SourceReserveOrderID', label: 'Source Reserve', type: 'number', hidden: false },
      { key: 'ApprovedBags', label: 'Approved Bags', type: 'number', hidden: false },
      { key: 'DestinationWarehouse', label: 'Warehouse', type: 'select', SearchType: 'select', hidden: false },
      { key: 'StatusName', label: 'Status', type: 'select', SearchType: 'select', hidden: false },
      { key: 'ApprovingTraderName', label: 'Approver', type: 'select', SearchType: 'select', hidden: false },
      { key: 'CreatedOrderID', label: 'Order #', type: 'number', hidden: false },
      { key: 'TargetReserveOrderID', label: 'Target Reserve', type: 'number', hidden: true },
      { key: 'RequestedByName', label: 'Requested By', hidden: true },
      { key: 'SourceCoffee', label: 'Source Coffee', hidden: true },
      { key: 'MatchType', label: 'Match', hidden: true },
      { key: 'MatchScore', label: 'Score', type: 'number', hidden: true },
      { key: 'TargetDate', label: 'Target Date', type: 'date', SearchType: 'date', hidden: true },
    ];
  }

  // ---------------------------------------------------------------------
  // Pulldowns
  // ---------------------------------------------------------------------

  openNewSwap() {
    this.openDetail(null, 'New Swap Request');
  }

  openSwap(row: SwapRow) {
    this.openDetail(row.SwapRequestID, `Swap ${row.SwapNumber}`);
  }

  private openDetail(swapRequestId: number | null, title: string) {
    const ref = this.pulldownService.open(SwapDetailComponent, {
      data: { SwapRequestID: swapRequestId },
      title,
      width: '88vw',
      maxWidth: '96vw',
      top: '0',
      maxHeight: '92vh',
      minHeight: '70vh'
    });

    // Always refresh on close — the pulldown's own "×" closes with no
    // result, so a truthy-result check would miss real saves (same fix
    // as Order Management).
    ref.afterClosed().pipe(take(1)).subscribe({
      next: async () => await this.loadList()
    });
  }

  // ---------------------------------------------------------------------
  // Audit export (6.2.48) — plain CSV download of every swap audit event
  // ---------------------------------------------------------------------

  async exportAuditReport() {
    this.Exporting = true;
    try {
      const rows = await lastValueFrom(this.swapData.getAuditReport());
      downloadCsv(rows ?? [], `swap-audit-${new Date().toISOString().substring(0, 10)}.csv`);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not export the audit report.');
    } finally {
      this.Exporting = false;
    }
  }
}
